/**
 * Panneau « Entraîner mes élèves / ma classe » — page d'une SKILLCARD, prof ou
 * admin seulement. Replié par défaut ; déplié, il liste les élèves liés (lien
 * actif) et les classes dont le niveau est compatible avec la carte
 * (`cardLevelCompatible`, miroir de `card_level_compatible`, migration 0354).
 * Sélection multiple puis « Envoyer » → RPC `assign_card_to_many` (0355) : la
 * carte devient gratuite pour chaque élève (0353), notifié par la file (0284).
 */
import { useState, type ReactNode } from 'react';
import { Check, ChevronDown, GraduationCap, Send, Users } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AvatarCircle, Button, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { cardLevelCompatible, schoolLevelRank } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import {
  useTutorClasses,
  useTutorStudents,
  type ClassSummary,
  type TutorLinkedStudent,
} from '@/lib/tutor/queries';

const db = supabase as unknown as SupabaseClient;
const LEVEL_LABEL = new Map(SCHOOL_LEVELS.map((l) => [l.value as string, l.label]));

/** Élèves liés ACTIFS dont le niveau convient à la carte, triés par nom. */
export function compatibleStudentsForCard(
  students: readonly TutorLinkedStudent[],
  cardLevels: readonly string[],
): TutorLinkedStudent[] {
  return students
    .filter((s) => s.status === 'active' && cardLevelCompatible(cardLevels, s.level))
    .sort((a, b) => a.display_name.localeCompare(b.display_name, 'fr'));
}

/** Classes non archivées, avec des élèves, de niveau compatible (sans niveau = oui). */
export function compatibleClassesForCard(
  classes: readonly ClassSummary[],
  cardLevels: readonly string[],
): ClassSummary[] {
  return classes
    .filter((c) => !c.archived_at && c.member_count > 0 && cardLevelCompatible(cardLevels, c.level))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

/**
 * Sépare « du niveau de la carte » (niveau de l'élève / de la classe parmi ceux
 * de la carte) et « autres compatibles » (niveau supérieur, ou sans niveau),
 * `others` triés du niveau le plus proche au plus loin. Carte sans niveau :
 * tout va dans `others`.
 */
export function splitByCardLevel<T>(
  items: readonly T[],
  levelOf: (item: T) => string | null,
  cardLevels: readonly string[],
): { exact: T[]; others: T[] } {
  const exact: T[] = [];
  const others: T[] = [];
  for (const item of items) {
    const level = levelOf(item);
    if (level && cardLevels.includes(level)) exact.push(item);
    else others.push(item);
  }
  // Les plus proches du niveau de la carte d'abord, sans niveau en dernier (tri stable).
  const rank = (item: T) => schoolLevelRank(levelOf(item)) ?? Number.MAX_SAFE_INTEGER;
  others.sort((a, b) => rank(a) - rank(b));
  return { exact, others };
}

export interface AssignManyResult {
  added: number;
  already: number;
  incompatible: number;
  not_allowed: number;
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Phrase de bilan après l'envoi. */
export function assignManySummary(r: AssignManyResult): string {
  const parts: string[] = [];
  if (r.added > 0) parts.push(`Envoyée à ${plural(r.added, 'élève', 'élèves')}`);
  if (r.already > 0) parts.push(`${plural(r.already, 'élève l’avait', 'élèves l’avaient')} déjà`);
  if (r.incompatible > 0)
    parts.push(`${plural(r.incompatible, 'élève', 'élèves')} d’un niveau trop bas`);
  if (r.not_allowed > 0)
    parts.push(`${plural(r.not_allowed, 'élève non relié', 'élèves non reliés')}`);
  return parts.length > 0 ? `${parts.join(' · ')}.` : 'Aucun élève à qui l’envoyer.';
}

/** Qui a déjà la carte, parmi les élèves et classes du prof (RPC 0356). */
export interface CardRecipientStatus {
  students: { id: string; assigned: boolean; owned: boolean }[];
  classes: { id: string; have: number; total: number }[];
}

export const cardRecipientStatusKey = (cardId: string) =>
  ['card-recipient-status', cardId] as const;

export function useCardRecipientStatus(cardId: string) {
  return useQuery({
    queryKey: cardRecipientStatusKey(cardId),
    queryFn: async (): Promise<CardRecipientStatus> => {
      const { data, error } = await db.rpc('get_card_recipient_status', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      const d = (data ?? {}) as Partial<CardRecipientStatus>;
      return { students: d.students ?? [], classes: d.classes ?? [] };
    },
  });
}

/** Mention sous un élève qui a déjà la carte (offerte prime sur achetée), sinon null. */
export function studentHoldingLabel(
  status: CardRecipientStatus | undefined,
  studentId: string,
): string | null {
  const row = status?.students.find((s) => s.id === studentId);
  if (!row) return null;
  if (row.assigned) return 'A déjà la carte (offerte)';
  if (row.owned) return 'A déjà la carte (achetée)';
  return null;
}

/** Mention sous une classe : toute la classe ou une partie l'a déjà, sinon null. */
export function classHolding(
  status: CardRecipientStatus | undefined,
  classId: string,
): { label: string; complete: boolean } | null {
  const row = status?.classes.find((c) => c.id === classId);
  if (!row || row.have === 0) return null;
  if (row.have >= row.total) return { label: 'Toute la classe a déjà la carte', complete: true };
  return {
    label: `${row.have} / ${row.total} élève${row.total > 1 ? 's' : ''} l’${row.have > 1 ? 'ont' : 'a'} déjà`,
    complete: false,
  };
}

export function useAssignCardToMany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cardId: string;
      studentIds: string[];
      classIds: string[];
    }): Promise<AssignManyResult> => {
      const { data, error } = await db.rpc('assign_card_to_many', {
        p_card_id: input.cardId,
        p_student_ids: input.studentIds,
        p_class_ids: input.classIds,
      });
      if (error) {
        throw new Error(
          error.message.includes('card_not_assignable')
            ? 'Cette SKILLCARD n’est pas publiée.'
            : 'Envoi impossible. Réessaie.',
        );
      }
      return data as AssignManyResult;
    },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['student-skillcards'] }),
        qc.invalidateQueries({ queryKey: ['card-catalog'] }),
        qc.invalidateQueries({ queryKey: ['card-recipient-status'] }),
      ]),
  });
}

/** Panneau rendu seulement pour un prof ou un admin. */
export function TrainStudentsPanel({
  cardId,
  cardLevels,
}: {
  cardId: string;
  cardLevels: readonly string[];
}) {
  const { session, profile } = useAuth();
  const tutorId = session?.user?.id ?? '';
  const [open, setOpen] = useState(false);
  if (!tutorId || (profile?.role !== 'tutor' && profile?.role !== 'admin')) return null;
  return (
    <section className="border-ink-200 rounded-2xl border bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="text-ink-900 font-display flex min-h-[2.75rem] w-full items-center justify-between gap-2 p-4 text-left text-base font-bold active:scale-[0.99]"
      >
        <span className="flex items-center gap-2">
          <span className="text-primary-500">
            <GraduationCap className="h-5 w-5" aria-hidden />
          </span>
          Entraîner mes élèves / ma classe
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && <TrainStudentsBody cardId={cardId} cardLevels={cardLevels} tutorId={tutorId} />}
    </section>
  );
}

function TrainStudentsBody({
  cardId,
  cardLevels,
  tutorId,
}: {
  cardId: string;
  cardLevels: readonly string[];
  tutorId: string;
}) {
  const students = useTutorStudents(tutorId);
  const classes = useTutorClasses(tutorId);
  const assign = useAssignCardToMany();
  const holders = useCardRecipientStatus(cardId);
  const [pickedStudents, setPickedStudents] = useState<Set<string>>(new Set());
  const [pickedClasses, setPickedClasses] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const studentList = compatibleStudentsForCard(students.data ?? [], cardLevels);
  const classList = compatibleClassesForCard(classes.data ?? [], cardLevels);
  const levelsLabel = cardLevels.map((l) => LEVEL_LABEL.get(l) ?? l).join(' / ');
  const total = pickedStudents.size + pickedClasses.size;
  const loading = students.isLoading || classes.isLoading;

  const toggle = (set: Set<string>, id: string, update: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    update(next);
    setMessage(null);
  };

  const send = async () => {
    setMessage(null);
    try {
      const res = await assign.mutateAsync({
        cardId,
        studentIds: [...pickedStudents],
        classIds: [...pickedClasses],
      });
      setMessage({ kind: res.added > 0 ? 'ok' : 'error', text: assignManySummary(res) });
      if (res.added > 0) {
        setPickedStudents(new Set());
        setPickedClasses(new Set());
      }
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof Error ? e.message : 'Envoi impossible.' });
    }
  };

  if (loading) {
    return (
      <div className="px-4 pb-4">
        <div className="bg-ink-100 h-16 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 pb-4">
      <p className="text-ink-500 text-sm">
        Choisis les élèves et les classes : la SKILLCARD leur est offerte (accès complet) et ils
        sont prévenus.
      </p>

      {classList.length > 0 && (
        <LevelGroups
          items={classList}
          levelOf={(c) => c.level}
          cardLevels={cardLevels}
          exactLabel={`Classes de ${levelsLabel}`}
          othersLabel="Autres classes compatibles"
          allLabel="Mes classes"
          render={(c, exact) => {
            const held = classHolding(holders.data, c.id);
            return (
              <CheckRow
                key={c.id}
                exact={exact}
                badge={held?.label ?? null}
                disabled={held?.complete ?? false}
                checked={pickedClasses.has(c.id)}
                onToggle={() => toggle(pickedClasses, c.id, setPickedClasses)}
                label={c.name}
                icon={
                  <span className="bg-primary-50 text-primary-600 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                    <Users className="h-4 w-4" aria-hidden />
                  </span>
                }
                detail={[
                  c.level ? (LEVEL_LABEL.get(c.level) ?? c.level) : null,
                  `${c.member_count} élève${c.member_count > 1 ? 's' : ''}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            );
          }}
        />
      )}

      {studentList.length === 0 ? (
        <p className="text-ink-500 text-sm">
          Aucun de tes élèves n’a un niveau compatible avec cette carte.
        </p>
      ) : (
        <LevelGroups
          items={studentList}
          levelOf={(st) => st.level}
          cardLevels={cardLevels}
          exactLabel={`Élèves de ${levelsLabel}`}
          othersLabel="Autres élèves compatibles"
          allLabel="Mes élèves"
          render={(st, exact) => {
            const held = studentHoldingLabel(holders.data, st.student_id);
            return (
              <CheckRow
                key={st.student_id}
                exact={exact}
                badge={held}
                disabled={held !== null}
                checked={pickedStudents.has(st.student_id)}
                onToggle={() => toggle(pickedStudents, st.student_id, setPickedStudents)}
                label={st.display_name}
                icon={<AvatarCircle name={st.display_name} size="sm" />}
                detail={st.level ? (LEVEL_LABEL.get(st.level) ?? st.level) : 'Niveau non renseigné'}
              />
            );
          }}
        />
      )}

      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={cn('text-sm', message.kind === 'ok' ? 'text-green-700' : 'text-error-600')}
        >
          {message.text}
        </p>
      )}

      <Button
        fullWidth
        disabled={total === 0 || assign.isPending}
        onClick={() => void send()}
        className="gap-2"
      >
        <Send className="h-4 w-4" aria-hidden />
        {assign.isPending
          ? 'Envoi…'
          : total === 0
            ? 'Envoyer'
            : `Envoyer (${[
                pickedClasses.size > 0 && plural(pickedClasses.size, 'classe', 'classes'),
                pickedStudents.size > 0 && plural(pickedStudents.size, 'élève', 'élèves'),
              ]
                .filter(Boolean)
                .join(' + ')})`}
      </Button>
    </div>
  );
}

function LevelGroups<T>({
  items,
  levelOf,
  cardLevels,
  exactLabel,
  othersLabel,
  allLabel,
  render,
}: {
  items: readonly T[];
  levelOf: (item: T) => string | null;
  cardLevels: readonly string[];
  exactLabel: string;
  othersLabel: string;
  allLabel: string;
  render: (item: T, exact: boolean) => ReactNode;
}) {
  const { exact, others } = splitByCardLevel(items, levelOf, cardLevels);
  return (
    <>
      {exact.length > 0 && (
        <fieldset className="space-y-1">
          <legend className="text-primary-600 mb-1 text-xs font-bold uppercase tracking-wide">
            {exactLabel}
          </legend>
          {exact.map((item) => render(item, true))}
        </fieldset>
      )}
      {others.length > 0 && (
        <fieldset className="space-y-1">
          <legend className="text-ink-500 mb-1 text-xs font-semibold uppercase tracking-wide">
            {exact.length > 0 ? othersLabel : allLabel}
          </legend>
          {others.map((item) => render(item, false))}
        </fieldset>
      )}
    </>
  );
}

function CheckRow({
  exact = false,
  badge = null,
  disabled = false,
  checked,
  onToggle,
  label,
  icon,
  detail,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  icon: ReactNode;
  detail: string | null;
  /** Du niveau exact de la carte : mis en avant. */
  exact?: boolean;
  /** Mention « a déjà la carte ». */
  badge?: string | null;
  /** Rien à envoyer (l'a déjà) : non cochable. */
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex min-h-[2.75rem] items-center gap-3 rounded-xl border px-3 py-2 transition',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer active:scale-[0.99]',
        checked
          ? 'border-primary-300 bg-primary-50'
          : exact
            ? 'border-primary-200 hover:bg-ink-50'
            : 'border-ink-100 hover:bg-ink-50',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        className="sr-only"
      />
      {icon}
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 block truncate text-sm font-semibold">{label}</span>
        {detail && <span className="text-ink-500 block text-xs">{detail}</span>}
        {badge && (
          <span className="ocean:bg-green-900/40 ocean:text-green-300 mt-0.5 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
            <Check className="h-3 w-3" aria-hidden />
            {badge}
          </span>
        )}
      </span>
      <span
        aria-hidden
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border',
          checked ? 'border-primary-500 bg-primary-500 text-white' : 'border-ink-300',
        )}
      >
        {checked && <Check className="h-4 w-4" />}
      </span>
    </label>
  );
}
